import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, FileCheck, Trash2, Clock, ChevronDown, ChevronUp, User, AlertTriangle, Loader2, Award, Package, X, Shirt, Briefcase, PenTool, Camera, Calculator, FileText, Scissors } from 'lucide-react';
import { MeasurementInput } from '../components/MeasurementInput';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { PhotoGallery } from '../components/PhotoGallery';
import { SketchCanvas } from '../components/SketchCanvas';
import { CostingModule } from '../components/CostingModule';
import { FabricCalculator } from '../components/FabricCalculator';
import { GarmentAnnotationsModule } from '../components/GarmentAnnotationsModule';
import ConfiguradorCamisaCompleto, { validarConfiguracionCamisaCompleta, getConfiguracionCamisaCompletaInicial } from '../components/ConfiguradorCamisaCompleto';
import SignatureCanvas from 'react-signature-canvas';
import MobileHeader from '../components/MobileHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const MeasurementSheetForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { user, canViewCosting } = useAuth();
  const sigCanvas = useRef(null);
  const [loading, setLoading] = useState(false);
  
  // Estados para validación de duplicados
  const [clienteEncontrado, setClienteEncontrado] = useState(null);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  
  // Lista de vendedores activos
  const [vendedoresActivos, setVendedoresActivos] = useState([]);
  
  // Estados para el configurador de camisa unificado
  const [erroresConfigCamisa, setErroresConfigCamisa] = useState([]);
  
  // ==================== SISTEMA DE CAMBIOS SIN GUARDAR ====================
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [lastSavedData, setLastSavedData] = useState(null);
  
  // Estados para asociación de cotización
  const [showBuscarCotizacion, setShowBuscarCotizacion] = useState(false);
  const [cotizacionesEncontradas, setCotizacionesEncontradas] = useState([]);
  const [buscandoCotizaciones, setBuscandoCotizaciones] = useState(false);
  const [busquedaCotizacion, setBusquedaCotizacion] = useState('');
  const [cotizacionAsociada, setCotizacionAsociada] = useState(null);
  const [showConfirmarSinCotizacion, setShowConfirmarSinCotizacion] = useState(false);
  
  // ==================== ALERTA DE CAMPOS PENDIENTES ====================
  const [showAlertaPendientes, setShowAlertaPendientes] = useState(false);
  const [validacionPendientes, setValidacionPendientes] = useState({ totalPendientes: 0, secciones: [] });
  const [accionPendiente, setAccionPendiente] = useState(null); // 'guardar' o 'finalizar'

  // Efecto para advertir al cerrar/recargar pestaña
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Hay cambios sin guardar. ¿Seguro que deseas salir?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);
  
  // Función para navegar con advertencia
  const navigateWithWarning = useCallback((path) => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
      setPendingNavigation(() => () => navigate(path));
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);
  
  // Función para buscar cotizaciones para asociar
  const buscarCotizacionesParaAsociar = async (termino) => {
    if (!termino || termino.length < 3) {
      setCotizacionesEncontradas([]);
      return;
    }
    
    setBuscandoCotizaciones(true);
    try {
      const params = new URLSearchParams();
      // Determinar tipo de búsqueda
      if (termino.toUpperCase().startsWith('COT-') || termino.toUpperCase().startsWith('JPM-')) {
        params.append('numero_cotizacion', termino);
      } else if (/^\d+$/.test(termino)) {
        params.append('documento', termino);
      } else if (termino.includes('@')) {
        params.append('correo', termino);
      } else {
        params.append('nombre', termino);
      }
      
      const response = await axios.get(`${API_URL}/cotizaciones-para-asociar?${params.toString()}`);
      setCotizacionesEncontradas(response.data.cotizaciones || []);
    } catch (error) {
      console.error('Error buscando cotizaciones:', error);
      toast.error('Error al buscar cotizaciones');
    } finally {
      setBuscandoCotizaciones(false);
    }
  };
  
  // Función para asociar una cotización a la ficha
  const asociarCotizacion = async (cotizacion) => {
    try {
      // Cargar datos completos de la cotización
      const response = await axios.get(`${API_URL}/cotizacion-para-ficha/${cotizacion.id}`);
      const datos = response.data;
      
      // Actualizar formData con los datos de la cotización
      setFormData(prev => ({
        ...prev,
        nombre: datos.datos_cliente?.nombre || prev.nombre,
        cedula: datos.datos_cliente?.numero_documento || prev.cedula,
        telefonos: datos.datos_cliente?.telefono || prev.telefonos,
        correo: datos.datos_cliente?.correo || prev.correo,
        tipo_documento: datos.datos_cliente?.tipo_documento || 'CC',
        cliente_id: datos.datos_cliente?.cliente_id || prev.cliente_id,
        fecha_evento: datos.datos_evento?.fecha_evento || prev.fecha_evento,
        wedding: datos.datos_evento?.tipo_evento || prev.wedding,
        vendedor_id: datos.datos_vendedor?.vendedor_id || prev.vendedor_id,
        vendedor_codigo: datos.datos_vendedor?.vendedor_codigo || prev.vendedor_codigo,
        vendedor_nombre: datos.datos_vendedor?.vendedor_nombre || prev.vendedor_nombre,
        quote_id: cotizacion.id,
        referencias_tela_cotizadas: datos.referencias_tela?.join(', ') || prev.referencias_tela_cotizadas,
        notas_produccion: datos.notas?.observaciones_internas || prev.notas_produccion
      }));
      
      setCotizacionAsociada({
        id: cotizacion.id,
        numero: cotizacion.numero,
        total: cotizacion.total,
        estado: cotizacion.estado,
        cliente_nombre: cotizacion.cliente_nombre
      });
      
      setShowBuscarCotizacion(false);
      setBusquedaCotizacion('');
      setCotizacionesEncontradas([]);
      setHasUnsavedChanges(true);
      
      toast.success(`Ficha asociada a cotización ${cotizacion.numero}`);
    } catch (error) {
      console.error('Error asociando cotización:', error);
      toast.error('Error al asociar cotización');
    }
  };
  
  // Función para desasociar cotización
  const desasociarCotizacion = () => {
    setFormData(prev => ({
      ...prev,
      quote_id: '',
      referencias_tela_cotizadas: ''
    }));
    setCotizacionAsociada(null);
    setHasUnsavedChanges(true);
    toast.info('Cotización desasociada');
  };
  
  // Memoizar props del canvas de firma para evitar re-renders
  const signatureCanvasProps = useMemo(() => ({
    className: 'w-full h-40',
    'data-testid': 'signature-canvas'
  }), []);
  
  const [formData, setFormData] = useState({
    pedido: '',
    actualizacion: new Date().toISOString().split('T')[0],
    nombre: '',
    cedula: '',
    wedding: '',
    correo: '',
    fecha_atencion: new Date().toISOString().split('T')[0],
    fecha_evento: '',
    telefonos: '',
    camisa: {
      // Nueva estructura unificada para el configurador completo
      ...getConfiguracionCamisaCompletaInicial()
    },
    blazer: {},
    pantalon: {},
    chaleco: {},
    chaqueta_gaban: {},
    // Estructura preparada para múltiples prendas configuradas
    prendas_configuradas: [],
    estado: 'borrador',
    firma_digital: '',
    notas_internas: '',
    garment_annotations: null,
    fabric_calculation: null,
    sketch: null,
    costing: null,
    photos: [],
    change_history: [],
    // Campos de integración
    cliente_id: '',
    tipo_documento: 'CC',
    quote_id: '',
    referencias_tela_cotizadas: '',
    notas_produccion: '',
    vendedor_id: '',
    vendedor_codigo: '',
    vendedor_nombre: ''
  });

  // Cargar vendedores activos
  useEffect(() => {
    const fetchVendedores = async () => {
      try {
        const response = await axios.get(`${API_URL}/vendedores/activos`);
        setVendedoresActivos(response.data || []);
      } catch (error) {
        console.error('Error al cargar vendedores:', error);
      }
    };
    fetchVendedores();
  }, []);

  // Cargar datos de cotización si viene de una cotización
  useEffect(() => {
    const quoteId = searchParams.get('quote_id');
    if (quoteId && id === 'new') {
      cargarDatosDeCotizacion(quoteId);
    }
  }, [searchParams, id]);

  const cargarDatosDeCotizacion = async (quoteId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/cotizacion-para-ficha/${quoteId}`);
      const datos = response.data;
      
      // Pre-llenar el formulario con datos de la cotización
      setFormData(prev => ({
        ...prev,
        nombre: datos.datos_cliente?.nombre || '',
        cedula: datos.datos_cliente?.numero_documento || '',
        correo: datos.datos_cliente?.correo || '',
        telefonos: datos.datos_cliente?.telefono || '',
        cliente_id: datos.datos_cliente?.cliente_id || '',
        tipo_documento: datos.datos_cliente?.tipo_documento || 'CC',
        fecha_evento: datos.datos_evento?.fecha_evento || '',
        vendedor_id: datos.datos_vendedor?.vendedor_id || '',
        vendedor_codigo: datos.datos_vendedor?.vendedor_codigo || '',
        vendedor_nombre: datos.datos_vendedor?.vendedor_nombre || '',
        quote_id: quoteId,
        referencias_tela_cotizadas: datos.referencias_tela?.join(', ') || '',
        notas_internas: datos.notas?.observaciones_internas || ''
      }));
      
      toast.success(`Datos trasladados de ${datos.cotizacion?.numero}`);
    } catch (error) {
      console.error('Error al cargar datos de cotización:', error);
      toast.error('No se pudieron cargar los datos de la cotización');
    } finally {
      setLoading(false);
    }
  };

  // Validar cliente existente por cédula, correo o teléfono
  const validarClienteExistente = useCallback(async (params) => {
    const { documento, correo, telefono } = params;
    
    // Validar que al menos un campo tenga valor suficiente
    const tieneDocumento = documento && documento.length >= 5;
    const tieneCorreo = correo && correo.includes('@') && correo.length >= 5;
    const tieneTelefono = telefono && telefono.replace(/\D/g, '').length >= 7;
    
    if (!tieneDocumento && !tieneCorreo && !tieneTelefono) {
      return;
    }
    
    // Si ya está asociado a un cliente, no buscar
    if (formData.cliente_id) {
      return;
    }
    
    setBuscandoCliente(true);
    try {
      const response = await axios.get(`${API_URL}/clientes/validar-duplicado`, {
        params: { 
          documento: tieneDocumento ? documento : undefined,
          correo: tieneCorreo ? correo : undefined,
          telefono: tieneTelefono ? telefono : undefined
        }
      });
      
      if (response.data.encontrado) {
        setClienteEncontrado(response.data);
        setShowClienteModal(true);
      } else {
        setClienteEncontrado(null);
      }
    } catch (error) {
      console.error('Error validando cliente:', error);
      setClienteEncontrado(null);
    } finally {
      setBuscandoCliente(false);
    }
  }, [formData.cliente_id]);

  // Asociar cliente existente con todos sus datos
  const asociarClienteExistente = () => {
    if (clienteEncontrado?.cliente) {
      const cliente = clienteEncontrado.cliente;
      const ultimaCot = clienteEncontrado.ultima_cotizacion;
      const ultimaFicha = clienteEncontrado.ultima_ficha;
      const referenciasTela = clienteEncontrado.referencias_tela || [];
      
      // Construir referencias de tela como texto
      const referenciasTexto = referenciasTela.length > 0 
        ? referenciasTela.join('\n') 
        : '';
      
      // Combinar notas de cotizaciones y fichas anteriores
      let notasAnteriores = '';
      if (ultimaCot?.observaciones_internas) {
        notasAnteriores += `[Cotización ${ultimaCot.numero}]: ${ultimaCot.observaciones_internas}\n`;
      }
      if (ultimaFicha?.notas_internas) {
        notasAnteriores += `[Ficha anterior]: ${ultimaFicha.notas_internas}\n`;
      }
      
      setFormData(prev => ({
        ...prev,
        nombre: cliente.nombre || prev.nombre,
        cedula: cliente.numero_documento || prev.cedula,
        correo: cliente.correo || prev.correo,
        telefonos: cliente.telefono || prev.telefonos,
        cliente_id: cliente.id_cliente || cliente.id,
        tipo_documento: cliente.tipo_documento || 'CC',
        // Traer datos de última cotización si existe
        ...(ultimaCot && {
          fecha_evento: ultimaCot.fecha_evento || prev.fecha_evento,
          vendedor_id: ultimaCot.vendedor_id || prev.vendedor_id,
          vendedor_codigo: ultimaCot.vendedor_codigo || prev.vendedor_codigo,
          vendedor_nombre: ultimaCot.vendedor_nombre || prev.vendedor_nombre,
          quote_id: ultimaCot.id || prev.quote_id
        }),
        // Agregar referencias de tela de cotizaciones anteriores
        referencias_tela_cotizadas: referenciasTexto || prev.referencias_tela_cotizadas,
        // Combinar notas
        notas_internas: notasAnteriores 
          ? (prev.notas_internas ? `${notasAnteriores}---\n${prev.notas_internas}` : notasAnteriores.trim())
          : prev.notas_internas
      }));
      
      toast.success(`Cliente ${cliente.id_cliente || cliente.nombre} asociado con su historial`);
      setShowClienteModal(false);
    }
  };
  
  // Desvincular cliente (para crear uno nuevo)
  const desvincularCliente = () => {
    setFormData(prev => ({
      ...prev,
      cliente_id: ''
    }));
    setClienteEncontrado(null);
    toast.info('Cliente desvinculado. Puede crear uno nuevo.');
  };

  useEffect(() => {
    if (id && id !== 'new') {
      fetchSheet();
    }
  }, [id]);

  const fetchSheet = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/sheets/${id}`);
      setFormData(response.data);
      if (response.data.firma_digital && sigCanvas.current) {
        sigCanvas.current.fromDataURL(response.data.firma_digital);
      }
    } catch (error) {
      toast.error('Error al cargar la ficha');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleSectionChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setFormData(prev => ({ ...prev, firma_digital: '' }));
      setHasUnsavedChanges(true);
    }
  };

  const saveSignature = () => {
    if (sigCanvas.current) {
      const dataURL = sigCanvas.current.toDataURL();
      setFormData(prev => ({ ...prev, firma_digital: dataURL }));
      setHasUnsavedChanges(true);
      toast.success('Firma guardada');
    }
  };

  // ==================== FUNCIONES HELPER PARA CALCULAR PENDIENTES ====================
  // Estas funciones se usan tanto en el modal de alerta como en los badges de las secciones
  
  const calcularPendientesBlazer = (blazer) => {
    if (!blazer) return { cantidad: 0, campos: [] };
    const tieneDatos = Object.values(blazer).some(v => v !== null && v !== undefined && v !== '');
    if (!tieneDatos) return { cantidad: 0, campos: [] };
    
    const pendientes = [];
    if (!blazer.pecho) pendientes.push('Pecho');
    if (!blazer.cintura) pendientes.push('Cintura');
    if (!blazer.base_cadera) pendientes.push('Base/Cadera');
    if (!blazer.largo_total) pendientes.push('Largo total');
    if (!blazer.hh) pendientes.push('HH');
    if (!blazer.manga) pendientes.push('Manga');
    if (!blazer.ancho_pecho) pendientes.push('Ancho de pecho');
    if (!blazer.ancho_espalda) pendientes.push('Ancho de espalda');
    if (!blazer.tela) pendientes.push('Tela');
    
    return { cantidad: pendientes.length, campos: pendientes };
  };
  
  const calcularPendientesPantalon = (pantalon) => {
    if (!pantalon) return { cantidad: 0, campos: [] };
    const tieneDatos = Object.values(pantalon).some(v => v !== null && v !== undefined && v !== '');
    if (!tieneDatos) return { cantidad: 0, campos: [] };
    
    const pendientes = [];
    if (!pantalon.cintura) pendientes.push('Cintura');
    if (!pantalon.base_cadera) pendientes.push('Base/Cadera');
    if (!pantalon.tiro) pendientes.push('Tiro');
    if (!pantalon.entrepierna) pendientes.push('Entrepierna');
    if (!pantalon.rodilla) pendientes.push('Rodilla');
    if (!pantalon.gemelos) pendientes.push('Gemelos');
    if (!pantalon.bota) pendientes.push('Bota');
    if (!pantalon.largo_total) pendientes.push('Largo total');
    if (!pantalon.tela) pendientes.push('Tela');
    
    return { cantidad: pendientes.length, campos: pendientes };
  };
  
  const calcularPendientesChaleco = (chaleco) => {
    if (!chaleco) return { cantidad: 0, campos: [] };
    const tieneDatos = Object.values(chaleco).some(v => v !== null && v !== undefined && v !== '');
    if (!tieneDatos) return { cantidad: 0, campos: [] };
    
    const pendientes = [];
    if (!chaleco.pecho) pendientes.push('Pecho');
    if (!chaleco.cintura) pendientes.push('Cintura');
    if (!chaleco.largo) pendientes.push('Largo');
    if (!chaleco.tela) pendientes.push('Tela');
    
    return { cantidad: pendientes.length, campos: pendientes };
  };
  
  const calcularPendientesChaqueta = (chaqueta) => {
    if (!chaqueta) return { cantidad: 0, campos: [] };
    const tieneDatos = Object.values(chaqueta).some(v => v !== null && v !== undefined && v !== '');
    if (!tieneDatos) return { cantidad: 0, campos: [] };
    
    const pendientes = [];
    if (!chaqueta.pecho) pendientes.push('Pecho');
    if (!chaqueta.cintura) pendientes.push('Cintura');
    if (!chaqueta.largo) pendientes.push('Largo');
    if (!chaqueta.manga) pendientes.push('Manga');
    if (!chaqueta.tela) pendientes.push('Tela');
    
    return { cantidad: pendientes.length, campos: pendientes };
  };
  
  const calcularPendientesCamisa = (camisa) => {
    if (!camisa) return { cantidad: 0, campos: [] };
    
    const pendientes = [];
    if (!camisa.cuello) pendientes.push('Cuello');
    if (!camisa.puños) pendientes.push('Puños');
    if (!camisa.botonadura) pendientes.push('Botonadura');
    if (!camisa.espalda) pendientes.push('Espalda');
    if (!camisa.bolsillo) pendientes.push('Bolsillo');
    if (!camisa.tipo_tela) pendientes.push('Tipo de tela');
    
    return { cantidad: pendientes.length, campos: pendientes };
  };

  // Función para validar campos pendientes y mostrar alerta
  const validarCamposPendientes = (mostrarAlerta = true) => {
    const camposPendientes = [];
    
    // --- DATOS BÁSICOS OBLIGATORIOS ---
    const datosBasicosPendientes = [];
    if (!formData.nombre?.trim()) datosBasicosPendientes.push('Nombre del cliente');
    if (!formData.cedula?.trim()) datosBasicosPendientes.push('Cédula/Documento');
    if (!formData.telefonos?.trim()) datosBasicosPendientes.push('Teléfono');
    if (!formData.vendedor_id) datosBasicosPendientes.push('Vendedor responsable');
    if (!formData.correo?.trim()) datosBasicosPendientes.push('Correo electrónico');
    if (!formData.fecha_evento) datosBasicosPendientes.push('Fecha del evento');
    
    if (datosBasicosPendientes.length > 0) {
      camposPendientes.push({
        seccion: 'DATOS BÁSICOS',
        icono: '📋',
        tipo: 'basico',
        cantidad: datosBasicosPendientes.length,
        campos: datosBasicosPendientes
      });
    }
    
    // --- CAMISA (siempre validar si hay configurador) ---
    const camisa = formData.camisa || {};
    const camisaPendientes = [];
    // Configuración de camisa
    if (!camisa.cuello) camisaPendientes.push('Cuello');
    if (!camisa.puños) camisaPendientes.push('Puños');
    if (!camisa.botonadura) camisaPendientes.push('Botonadura');
    if (!camisa.espalda) camisaPendientes.push('Espalda');
    if (!camisa.bolsillo) camisaPendientes.push('Bolsillo');
    if (!camisa.tipo_tela) camisaPendientes.push('Tipo de tela');
    
    if (camisaPendientes.length > 0) {
      camposPendientes.push({
        seccion: 'CAMISA',
        icono: '👔',
        tipo: 'camisa',
        cantidad: camisaPendientes.length,
        campos: camisaPendientes
      });
    }
    
    // --- BLAZER (si tiene CUALQUIER dato, validar TODOS los campos) ---
    const blazer = formData.blazer || {};
    const blazerTieneDatos = Object.values(blazer).some(v => v !== null && v !== undefined && v !== '');
    
    if (blazerTieneDatos) {
      const blazerPendientes = [];
      // Medidas obligatorias del blazer
      if (!blazer.pecho) blazerPendientes.push('Pecho');
      if (!blazer.cintura) blazerPendientes.push('Cintura');
      if (!blazer.base_cadera) blazerPendientes.push('Base/Cadera');
      if (!blazer.largo_total) blazerPendientes.push('Largo total');
      if (!blazer.hh) blazerPendientes.push('HH');
      if (!blazer.manga) blazerPendientes.push('Manga');
      if (!blazer.ancho_pecho) blazerPendientes.push('Ancho de pecho');
      if (!blazer.ancho_espalda) blazerPendientes.push('Ancho de espalda');
      if (!blazer.tela) blazerPendientes.push('Tela');
      
      if (blazerPendientes.length > 0) {
        camposPendientes.push({
          seccion: 'BLAZER',
          icono: '🧥',
          tipo: 'blazer',
          cantidad: blazerPendientes.length,
          campos: blazerPendientes
        });
      }
    }
    
    // --- PANTALÓN (si tiene CUALQUIER dato, validar TODOS los campos) ---
    const pantalon = formData.pantalon || {};
    const pantalonTieneDatos = Object.values(pantalon).some(v => v !== null && v !== undefined && v !== '');
    
    if (pantalonTieneDatos) {
      const pantalonPendientes = [];
      if (!pantalon.cintura) pantalonPendientes.push('Cintura');
      if (!pantalon.base_cadera) pantalonPendientes.push('Base/Cadera');
      if (!pantalon.tiro) pantalonPendientes.push('Tiro');
      if (!pantalon.entrepierna) pantalonPendientes.push('Entrepierna');
      if (!pantalon.rodilla) pantalonPendientes.push('Rodilla');
      if (!pantalon.gemelos) pantalonPendientes.push('Gemelos');
      if (!pantalon.bota) pantalonPendientes.push('Bota');
      if (!pantalon.largo_total) pantalonPendientes.push('Largo total');
      if (!pantalon.tela) pantalonPendientes.push('Tela');
      
      if (pantalonPendientes.length > 0) {
        camposPendientes.push({
          seccion: 'PANTALÓN',
          icono: '👖',
          tipo: 'pantalon',
          cantidad: pantalonPendientes.length,
          campos: pantalonPendientes
        });
      }
    }
    
    // --- CHALECO (si tiene CUALQUIER dato, validar TODOS los campos) ---
    const chaleco = formData.chaleco || {};
    const chalecoTieneDatos = Object.values(chaleco).some(v => v !== null && v !== undefined && v !== '');
    
    if (chalecoTieneDatos) {
      const chalecoPendientes = [];
      if (!chaleco.pecho) chalecoPendientes.push('Pecho');
      if (!chaleco.cintura) chalecoPendientes.push('Cintura');
      if (!chaleco.largo) chalecoPendientes.push('Largo');
      if (!chaleco.tela) chalecoPendientes.push('Tela');
      
      if (chalecoPendientes.length > 0) {
        camposPendientes.push({
          seccion: 'CHALECO',
          icono: '🦺',
          tipo: 'chaleco',
          cantidad: chalecoPendientes.length,
          campos: chalecoPendientes
        });
      }
    }
    
    // --- CHAQUETA/GABÁN (si tiene CUALQUIER dato, validar campos) ---
    const chaqueta = formData.chaqueta_gaban || {};
    const chaquetaTieneDatos = Object.values(chaqueta).some(v => v !== null && v !== undefined && v !== '');
    
    if (chaquetaTieneDatos) {
      const chaquetaPendientes = [];
      if (!chaqueta.pecho) chaquetaPendientes.push('Pecho');
      if (!chaqueta.cintura) chaquetaPendientes.push('Cintura');
      if (!chaqueta.largo) chaquetaPendientes.push('Largo');
      if (!chaqueta.manga) chaquetaPendientes.push('Manga');
      if (!chaqueta.tela) chaquetaPendientes.push('Tela');
      
      if (chaquetaPendientes.length > 0) {
        camposPendientes.push({
          seccion: 'CHAQUETA/GABÁN',
          icono: '🧥',
          tipo: 'chaqueta',
          cantidad: chaquetaPendientes.length,
          campos: chaquetaPendientes
        });
      }
    }

    // Calcular total de campos pendientes
    const totalPendientes = camposPendientes.reduce((acc, sec) => acc + sec.cantidad, 0);

    return {
      hayPendientes: totalPendientes > 0,
      totalPendientes,
      secciones: camposPendientes,
      // Para compatibilidad con código anterior
      campos: camposPendientes.map(sec => `${sec.icono} ${sec.seccion} (${sec.cantidad}): ${sec.campos.join(', ')}`),
      mensaje: ''
    };
  };

  const handleSubmit = async (e, finalizar = false, forzarGuardado = false) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.cedula) {
      toast.error('Nombre y cédula son obligatorios');
      return;
    }

    // Validación de campos pendientes al FINALIZAR (a menos que se fuerce)
    if (finalizar && !forzarGuardado) {
      const validacion = validarCamposPendientes(true);
      if (validacion.hayPendientes) {
        // Mostrar modal visual de alerta con la nueva estructura
        setValidacionPendientes(validacion);
        setAccionPendiente('finalizar');
        setShowAlertaPendientes(true);
        return;
      }
    }
    
    // Validación: Si se intenta finalizar sin cotización asociada, mostrar confirmación
    if (finalizar && !formData.quote_id && !showConfirmarSinCotizacion) {
      setShowConfirmarSinCotizacion(true);
      return;
    }

    try {
      setLoading(true);
      // Convertir campos vacíos a null para que Pydantic EmailStr no falle
      const submitData = {
        ...formData,
        correo: formData.correo?.trim() || null,
        estado: finalizar ? 'finalizada' : formData.estado
      };

      if (id && id !== 'new') {
        await axios.put(`${API_URL}/sheets/${id}`, submitData);
        toast.success('Ficha actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/sheets`, submitData);
        toast.success('Ficha creada exitosamente');
      }
      setHasUnsavedChanges(false); // Marcar como guardado
      setShowConfirmarSinCotizacion(false);
      navigate('/dashboard');
    } catch (error) {
      // Handle Pydantic validation errors which return an array
      const detail = error.response?.data?.detail;
      let errorMessage = 'Error al guardar la ficha';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic returns [{loc: [...], msg: "...", type: "..."}]
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id && id !== 'new') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm uppercase tracking-widest text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-0">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header Premium Negro */}
      <header className="bg-black sticky top-0 z-20 hidden md:block">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigateWithWarning('/dashboard')}
                className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/60 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Volver</span>
              </button>
              <div className="w-px h-6 bg-white/20" />
              <h1 className="font-serif text-lg text-white">
                {id && id !== 'new' ? 'Editar Ficha' : 'Nueva Ficha'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {id && id !== 'new' && (
                <span className={`px-2 py-1 text-[10px] uppercase tracking-wider ${
                  formData.estado === 'finalizada' 
                    ? 'bg-[#D4AF37]/20 text-[#D4AF37]' 
                    : 'bg-white/10 text-white/60'
                }`}>
                  {formData.estado}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 md:py-8">

        <div className="bg-white shadow-2xl border border-border/20 relative overflow-hidden" data-testid="measurement-sheet-form">
          <div className="p-4 sm:p-6 md:p-12 lg:p-16">
            <div className="text-center mb-12">
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl text-black mb-2">HOJA DE MEDIDAS</h1>
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">ATELIER — JPM</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8 pb-8 border-b border-border">
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1 block">Código</label>
                <p className="text-sm font-medium">ES-JPM-004</p>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1 block">Versión</label>
                <p className="text-sm font-medium">1</p>
              </div>
              <div className="sm:col-span-2">
                <MeasurementInput
                  label="Actualización"
                  type="date"
                  name="actualizacion"
                  value={formData.actualizacion}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Banner de cliente vinculado */}
              {formData.cliente_id && (
                <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Cliente vinculado: {formData.cliente_id}</p>
                      <p className="text-xs text-emerald-600">Los datos están sincronizados con el historial del cliente</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={desvincularCliente}
                    className="text-xs text-emerald-700 hover:text-emerald-900 underline"
                  >
                    Desvincular
                  </button>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mb-12">
                <MeasurementInput
                  label="Nombre *"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  placeholder="Nombre completo del cliente"
                />
                <div className="relative">
                  <MeasurementInput
                    label="Cédula / Documento *"
                    name="cedula"
                    value={formData.cedula}
                    onChange={(e) => {
                      handleInputChange(e);
                    }}
                    onBlur={(e) => {
                      if (id === 'new' && e.target.value.length >= 5) {
                        validarClienteExistente({ documento: e.target.value });
                      }
                    }}
                    placeholder="Número de identificación"
                  />
                  {buscandoCliente && (
                    <div className="absolute right-2 top-8">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    </div>
                  )}
                </div>
                <MeasurementInput
                  label="Wedding"
                  name="wedding"
                  value={formData.wedding}
                  onChange={handleInputChange}
                  placeholder="Evento especial"
                />
                <div className="relative">
                  <MeasurementInput
                    label="Correo"
                    type="email"
                    name="correo"
                    value={formData.correo}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      if (id === 'new' && e.target.value && e.target.value.includes('@')) {
                        validarClienteExistente({ correo: e.target.value });
                      }
                    }}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <MeasurementInput
                  label="Fecha de Atención"
                  type="date"
                  name="fecha_atencion"
                  value={formData.fecha_atencion}
                  onChange={handleInputChange}
                />
                <MeasurementInput
                  label="Fecha del Evento"
                  type="date"
                  name="fecha_evento"
                  value={formData.fecha_evento}
                  onChange={handleInputChange}
                />
                <div className="relative">
                  <MeasurementInput
                    label="Teléfonos"
                    name="telefonos"
                    value={formData.telefonos}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      if (id === 'new' && e.target.value && e.target.value.replace(/\D/g, '').length >= 7) {
                        validarClienteExistente({ telefono: e.target.value });
                      }
                    }}
                    placeholder="Números de contacto"
                  />
                </div>
                <MeasurementInput
                  label="Pedido"
                  name="pedido"
                  value={formData.pedido}
                  onChange={handleInputChange}
                  placeholder="Número de pedido"
                />
                {/* Vendedor responsable */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-warm-700 mb-1">
                    <Award className="w-3 h-3 inline mr-1" />
                    Vendedor Responsable
                  </label>
                  <select
                    value={formData.vendedor_id || ''}
                    onChange={(e) => {
                      const vendedorId = e.target.value;
                      const vendedor = vendedoresActivos.find(v => v.id === vendedorId);
                      setFormData(prev => ({
                        ...prev,
                        vendedor_id: vendedorId,
                        vendedor_codigo: vendedor?.id_vendedor || '',
                        vendedor_nombre: vendedor?.nombre || ''
                      }));
                    }}
                    className="w-full px-3 py-2 border border-warm-200 rounded-lg bg-white focus:outline-none focus:border-primary text-sm"
                    data-testid="vendedor-select-ficha"
                  >
                    <option value="">Seleccionar vendedor...</option>
                    {vendedoresActivos.map(vendedor => (
                      <option key={vendedor.id} value={vendedor.id}>
                        {vendedor.nombre} ({vendedor.id_vendedor})
                      </option>
                    ))}
                  </select>
                  {formData.vendedor_codigo && (
                    <p className="text-xs text-emerald-600 mt-1">
                      Asignado: {formData.vendedor_codigo}
                    </p>
                  )}
                </div>
              </div>
              
              {/* ==================== SECCIÓN: ASOCIACIÓN DE COTIZACIÓN ==================== */}
              <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-blue-800 flex items-center gap-2">
                    <FileCheck className="w-4 h-4" />
                    Asociación de Cotización
                  </h4>
                  {formData.quote_id ? (
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                      ✓ Asociada
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                      Sin asociar
                    </span>
                  )}
                </div>
                
                {formData.quote_id || cotizacionAsociada ? (
                  // Cotización asociada
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          {cotizacionAsociada?.numero || `Cotización ${formData.quote_id?.slice(0,8)}`}
                        </p>
                        <p className="text-xs text-blue-600">
                          {cotizacionAsociada?.cliente_nombre || formData.nombre}
                          {cotizacionAsociada?.total && ` · $${cotizacionAsociada.total.toLocaleString('es-CO')}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => window.open(`/quotes/${formData.quote_id}`, '_blank')}
                          className="px-3 py-1.5 text-xs bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          Ver cotización
                        </button>
                        {formData.estado === 'borrador' && (
                          <button
                            type="button"
                            onClick={desasociarCotizacion}
                            className="px-3 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
                          >
                            Desasociar
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Referencias de tela de la cotización */}
                    {formData.referencias_tela_cotizadas && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs font-medium text-amber-700 mb-1">
                          <Package className="w-3 h-3 inline mr-1" />
                          Referencias de Tela
                        </p>
                        <p className="text-sm text-amber-800">{formData.referencias_tela_cotizadas}</p>
                      </div>
                    )}
                    
                    {/* Notas de producción */}
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">
                        Notas internas de producción
                      </label>
                      <textarea
                        name="notas_produccion"
                        value={formData.notas_produccion || ''}
                        onChange={handleInputChange}
                        rows={2}
                        className="w-full px-3 py-2 border border-blue-200 rounded-lg bg-white focus:outline-none focus:border-blue-500 text-sm"
                        placeholder="Notas adicionales para producción..."
                      />
                    </div>
                  </div>
                ) : (
                  // Sin cotización asociada
                  <div className="space-y-3">
                    <p className="text-sm text-blue-700">
                      ¿Esta ficha está asociada a una cotización existente?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setShowBuscarCotizacion(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                        data-testid="btn-buscar-cotizacion"
                      >
                        <FileCheck className="w-4 h-4" />
                        Buscar y asociar cotización
                      </button>
                      <button
                        type="button"
                        onClick={() => toast.info('Esta ficha se guardará como independiente (sin cotización asociada)')}
                        className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors text-sm"
                      >
                        Continuar sin asociar
                      </button>
                    </div>
                    <p className="text-xs text-blue-600 italic">
                      Al asociar una cotización, se cargarán automáticamente los datos del cliente, referencias de tela y notas.
                    </p>
                  </div>
                )}
              </div>

              <CollapsibleSection 
                title="CAMISA" 
                icon={Shirt} 
                defaultOpen={false}
                summary={formData.camisa?.cuello ? `Cuello: ${formData.camisa.cuello}, Puño: ${formData.camisa.puños || 'Sin definir'}` : 'Toca para configurar'}
                status={calcularPendientesCamisa(formData.camisa).cantidad === 0 ? 'complete' : formData.camisa?.cuello ? 'incomplete' : 'empty'}
                pendingCount={calcularPendientesCamisa(formData.camisa).cantidad}
              >
                {/* Configurador Unificado de Camisa */}
                <div className="-mx-5">
                  <ConfiguradorCamisaCompleto
                    datos={formData.camisa || {}}
                    onChange={(nuevaConfigCamisa) => {
                      setFormData(prev => ({
                        ...prev,
                        camisa: nuevaConfigCamisa
                      }));
                      setErroresConfigCamisa([]);
                      setHasUnsavedChanges(true);
                    }}
                    clienteNombre={formData.nombre}
                    numeroPedido={formData.pedido}
                    errores={erroresConfigCamisa}
                  />
                </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="BLAZER" 
                icon={Briefcase} 
                defaultOpen={false}
                summary={formData.blazer?.pecho ? `Pecho: ${formData.blazer.pecho}, Cintura: ${formData.blazer.cintura || '-'}` : 'Sin medidas'}
                status={calcularPendientesBlazer(formData.blazer).cantidad === 0 && formData.blazer?.pecho ? 'complete' : formData.blazer?.pecho ? 'incomplete' : 'empty'}
                pendingCount={calcularPendientesBlazer(formData.blazer).cantidad}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-10">
                <MeasurementInput
                  label="Pecho"
                  name="blazer_pecho"
                  value={formData.blazer?.pecho}
                  onChange={(e) => handleSectionChange('blazer', 'pecho', e.target.value)}
                />
                <MeasurementInput
                  label="Cintura"
                  name="blazer_cintura"
                  value={formData.blazer?.cintura}
                  onChange={(e) => handleSectionChange('blazer', 'cintura', e.target.value)}
                />
                <MeasurementInput
                  label="Base/Cadera"
                  name="blazer_base_cadera"
                  value={formData.blazer?.base_cadera}
                  onChange={(e) => handleSectionChange('blazer', 'base_cadera', e.target.value)}
                />
                <MeasurementInput
                  label="Largo Total"
                  name="blazer_largo_total"
                  value={formData.blazer?.largo_total}
                  onChange={(e) => handleSectionChange('blazer', 'largo_total', e.target.value)}
                />
                <MeasurementInput
                  label="HH"
                  name="blazer_hh"
                  value={formData.blazer?.hh}
                  onChange={(e) => handleSectionChange('blazer', 'hh', e.target.value)}
                />
                <MeasurementInput
                  label="Manga"
                  name="blazer_manga"
                  value={formData.blazer?.manga}
                  onChange={(e) => handleSectionChange('blazer', 'manga', e.target.value)}
                />
                <MeasurementInput
                  label="Ancho de Pecho"
                  name="blazer_ancho_pecho"
                  value={formData.blazer?.ancho_pecho}
                  onChange={(e) => handleSectionChange('blazer', 'ancho_pecho', e.target.value)}
                />
                <MeasurementInput
                  label="Ancho de Espalda"
                  name="blazer_ancho_espalda"
                  value={formData.blazer?.ancho_espalda}
                  onChange={(e) => handleSectionChange('blazer', 'ancho_espalda', e.target.value)}
                />
                <MeasurementInput
                  label="Tela"
                  name="blazer_tela"
                  value={formData.blazer?.tela}
                  onChange={(e) => handleSectionChange('blazer', 'tela', e.target.value)}
                />
                <MeasurementInput
                  label="Forro"
                  name="blazer_forro"
                  value={formData.blazer?.forro}
                  onChange={(e) => handleSectionChange('blazer', 'forro', e.target.value)}
                />
              </div>
              <div className="mt-6">
                <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
                  Observaciones
                </label>
                <textarea
                  name="blazer_observaciones"
                  value={formData.blazer?.observaciones}
                  onChange={(e) => handleSectionChange('blazer', 'observaciones', e.target.value)}
                  rows="3"
                  className="w-full rounded-none border border-input bg-transparent px-4 py-3 text-sm shadow-none transition-colors focus-visible:outline-none focus-visible:border-black resize-none"
                  placeholder="Notas adicionales sobre el blazer..."
                />
              </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="PANTALÓN" 
                icon={Scissors} 
                defaultOpen={false}
                summary={formData.pantalon?.cintura ? `Cintura: ${formData.pantalon.cintura}, Largo: ${formData.pantalon.largo_total || '-'}` : 'Sin medidas'}
                status={calcularPendientesPantalon(formData.pantalon).cantidad === 0 && formData.pantalon?.cintura ? 'complete' : formData.pantalon?.cintura ? 'incomplete' : 'empty'}
                pendingCount={calcularPendientesPantalon(formData.pantalon).cantidad}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-10">
                <MeasurementInput
                  label="Cintura"
                  name="pantalon_cintura"
                  value={formData.pantalon?.cintura}
                  onChange={(e) => handleSectionChange('pantalon', 'cintura', e.target.value)}
                />
                <MeasurementInput
                  label="Base/Cadera"
                  name="pantalon_base_cadera"
                  value={formData.pantalon?.base_cadera}
                  onChange={(e) => handleSectionChange('pantalon', 'base_cadera', e.target.value)}
                />
                <MeasurementInput
                  label="Tiro"
                  name="pantalon_tiro"
                  value={formData.pantalon?.tiro}
                  onChange={(e) => handleSectionChange('pantalon', 'tiro', e.target.value)}
                />
                <MeasurementInput
                  label="Entrepierna"
                  name="pantalon_entrepierna"
                  value={formData.pantalon?.entrepierna}
                  onChange={(e) => handleSectionChange('pantalon', 'entrepierna', e.target.value)}
                />
                <MeasurementInput
                  label="Rodilla"
                  name="pantalon_rodilla"
                  value={formData.pantalon?.rodilla}
                  onChange={(e) => handleSectionChange('pantalon', 'rodilla', e.target.value)}
                />
                <MeasurementInput
                  label="Gemelos"
                  name="pantalon_gemelos"
                  value={formData.pantalon?.gemelos}
                  onChange={(e) => handleSectionChange('pantalon', 'gemelos', e.target.value)}
                />
                <MeasurementInput
                  label="Bota"
                  name="pantalon_bota"
                  value={formData.pantalon?.bota}
                  onChange={(e) => handleSectionChange('pantalon', 'bota', e.target.value)}
                />
                <MeasurementInput
                  label="Largo Total"
                  name="pantalon_largo_total"
                  value={formData.pantalon?.largo_total}
                  onChange={(e) => handleSectionChange('pantalon', 'largo_total', e.target.value)}
                />
              </div>
              <div className="mt-6">
                <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
                  Observaciones
                </label>
                <textarea
                  name="pantalon_observaciones"
                  value={formData.pantalon?.observaciones}
                  onChange={(e) => handleSectionChange('pantalon', 'observaciones', e.target.value)}
                  rows="3"
                  className="w-full rounded-none border border-input bg-transparent px-4 py-3 text-sm shadow-none transition-colors focus-visible:outline-none focus-visible:border-black resize-none"
                  placeholder="Notas adicionales sobre el pantalón..."
                />
              </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="CHALECO" 
                defaultOpen={false}
                summary={formData.chaleco?.pecho ? `Pecho: ${formData.chaleco.pecho}, Largo: ${formData.chaleco.largo || '-'}` : 'Sin medidas'}
                status={calcularPendientesChaleco(formData.chaleco).cantidad === 0 && formData.chaleco?.pecho ? 'complete' : formData.chaleco?.pecho ? 'incomplete' : 'empty'}
                pendingCount={calcularPendientesChaleco(formData.chaleco).cantidad}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-10">
                <MeasurementInput
                  label="Pecho"
                  name="chaleco_pecho"
                  value={formData.chaleco?.pecho}
                  onChange={(e) => handleSectionChange('chaleco', 'pecho', e.target.value)}
                />
                <MeasurementInput
                  label="Cintura"
                  name="chaleco_cintura"
                  value={formData.chaleco?.cintura}
                  onChange={(e) => handleSectionChange('chaleco', 'cintura', e.target.value)}
                />
                <MeasurementInput
                  label="Base"
                  name="chaleco_base_cadera"
                  value={formData.chaleco?.base_cadera}
                  onChange={(e) => handleSectionChange('chaleco', 'base_cadera', e.target.value)}
                />
                <MeasurementInput
                  label="Largo Total"
                  name="chaleco_largo_total"
                  value={formData.chaleco?.largo_total}
                  onChange={(e) => handleSectionChange('chaleco', 'largo_total', e.target.value)}
                />
                <MeasurementInput
                  label="Tela"
                  name="chaleco_tela"
                  value={formData.chaleco?.tela}
                  onChange={(e) => handleSectionChange('chaleco', 'tela', e.target.value)}
                />
                <MeasurementInput
                  label="Forro"
                  name="chaleco_forro"
                  value={formData.chaleco?.forro}
                  onChange={(e) => handleSectionChange('chaleco', 'forro', e.target.value)}
                />
              </div>
              <div className="mt-6">
                <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
                  Observaciones
                </label>
                <textarea
                  name="chaleco_observaciones"
                  value={formData.chaleco?.observaciones}
                  onChange={(e) => handleSectionChange('chaleco', 'observaciones', e.target.value)}
                  rows="3"
                  className="w-full rounded-none border border-input bg-transparent px-4 py-3 text-sm shadow-none transition-colors focus-visible:outline-none focus-visible:border-black resize-none"
                  placeholder="Notas adicionales sobre el chaleco..."
                />
              </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="CHAQUETA / GABÁN" 
                defaultOpen={false}
                summary={formData.chaqueta_gaban?.pecho ? `Pecho: ${formData.chaqueta_gaban.pecho}, Largo: ${formData.chaqueta_gaban.largo || '-'}` : 'Sin medidas'}
                status={calcularPendientesChaqueta(formData.chaqueta_gaban).cantidad === 0 && formData.chaqueta_gaban?.pecho ? 'complete' : formData.chaqueta_gaban?.pecho ? 'incomplete' : 'empty'}
                pendingCount={calcularPendientesChaqueta(formData.chaqueta_gaban).cantidad}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4 md:gap-x-8 gap-y-6 md:gap-y-10">
                <MeasurementInput
                  label="Pecho"
                  name="chaqueta_gaban_pecho"
                  value={formData.chaqueta_gaban?.pecho}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'pecho', e.target.value)}
                />
                <MeasurementInput
                  label="Cintura"
                  name="chaqueta_gaban_cintura"
                  value={formData.chaqueta_gaban?.cintura}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'cintura', e.target.value)}
                />
                <MeasurementInput
                  label="HH"
                  name="chaqueta_gaban_hh"
                  value={formData.chaqueta_gaban?.hh}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'hh', e.target.value)}
                />
                <MeasurementInput
                  label="Manga"
                  name="chaqueta_gaban_manga"
                  value={formData.chaqueta_gaban?.manga}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'manga', e.target.value)}
                />
                <MeasurementInput
                  label="Base o Cadera"
                  name="chaqueta_gaban_base_cadera"
                  value={formData.chaqueta_gaban?.base_cadera}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'base_cadera', e.target.value)}
                />
                <MeasurementInput
                  label="Ancho Espalda"
                  name="chaqueta_gaban_ancho_espalda"
                  value={formData.chaqueta_gaban?.ancho_espalda}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'ancho_espalda', e.target.value)}
                />
                <MeasurementInput
                  label="Ancho Pecho"
                  name="chaqueta_gaban_ancho_pecho"
                  value={formData.chaqueta_gaban?.ancho_pecho}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'ancho_pecho', e.target.value)}
                />
                <MeasurementInput
                  label="Largo"
                  name="chaqueta_gaban_largo_total"
                  value={formData.chaqueta_gaban?.largo_total}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'largo_total', e.target.value)}
                />
                <MeasurementInput
                  label="Tela"
                  name="chaqueta_gaban_tela"
                  value={formData.chaqueta_gaban?.tela}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'tela', e.target.value)}
                />
                <MeasurementInput
                  label="Forro"
                  name="chaqueta_gaban_forro"
                  value={formData.chaqueta_gaban?.forro}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'forro', e.target.value)}
                />
              </div>
              <div className="mt-6">
                <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
                  Observaciones
                </label>
                <textarea
                  name="chaqueta_gaban_observaciones"
                  value={formData.chaqueta_gaban?.observaciones}
                  onChange={(e) => handleSectionChange('chaqueta_gaban', 'observaciones', e.target.value)}
                  rows="3"
                  className="w-full rounded-none border border-input bg-transparent px-4 py-3 text-sm shadow-none transition-colors focus-visible:outline-none focus-visible:border-black resize-none"
                  placeholder="Notas adicionales sobre la chaqueta/gabán..."
                />
              </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="SILUETAS DE PRENDAS" 
                icon={PenTool} 
                defaultOpen={false}
                summary={formData.garment_annotations?.length ? `${formData.garment_annotations.length} anotación(es)` : 'Sin anotaciones'}
                status={formData.garment_annotations?.length ? 'complete' : 'empty'}
              >
                <GarmentAnnotationsModule
                  initialAnnotations={formData.garment_annotations}
                  onSave={(annotationsData) => {
                    setFormData(prev => ({
                      ...prev,
                      garment_annotations: annotationsData
                    }));
                    toast.success('Anotaciones guardadas');
                  }}
                />
              </CollapsibleSection>

              <CollapsibleSection 
                title="CÁLCULO DE METRAJE" 
                icon={Calculator} 
                defaultOpen={false}
                summary={formData.fabric_calculation?.total_metros ? `${formData.fabric_calculation.total_metros}m calculados` : 'Sin calcular'}
                status={formData.fabric_calculation?.total_metros ? 'complete' : 'empty'}
              >
                <FabricCalculator
                  measurements={formData}
                  initialCalculation={formData.fabric_calculation}
                  onSave={(calculationData) => {
                    setFormData(prev => ({
                      ...prev,
                      fabric_calculation: calculationData
                    }));
                  }}
                />
              </CollapsibleSection>

              <CollapsibleSection 
                title="CROQUIS DEL DISEÑO" 
                icon={PenTool} 
                defaultOpen={false}
                summary={formData.sketch?.imageData ? 'Croquis guardado' : 'Sin croquis'}
                status={formData.sketch?.imageData ? 'complete' : 'empty'}
              >
                <SketchCanvas
                  initialSketch={formData.sketch}
                  onSave={(sketchData) => {
                    setFormData(prev => ({
                      ...prev,
                      sketch: {
                        data: sketchData.data,           // JSON paths para re-cargar
                        imageData: sketchData.imagePreview, // Imagen PNG base64 para PDF
                        description: sketchData.description
                      }
                    }));
                  }}
                />
              </CollapsibleSection>

              <CollapsibleSection 
                title="NOTAS INTERNAS" 
                icon={FileText} 
                defaultOpen={false}
                summary={formData.notas_internas ? formData.notas_internas.substring(0, 50) + (formData.notas_internas.length > 50 ? '...' : '') : 'Sin notas'}
                status={formData.notas_internas ? 'complete' : 'empty'}
              >
                <div>
                  <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-1.5 block">
                    Notas adicionales (uso interno)
                  </label>
                  <textarea
                    name="notas_internas"
                    value={formData.notas_internas}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full rounded-none border border-input bg-transparent px-4 py-3 text-sm shadow-none transition-colors focus-visible:outline-none focus-visible:border-black resize-none"
                    placeholder="Observaciones generales, instrucciones especiales, etc..."
                    data-testid="notas-internas"
                  />
                </div>
              </CollapsibleSection>

              <CollapsibleSection 
                title="FIRMA DIGITAL" 
                defaultOpen={false}
                summary={formData.firma?.data ? 'Firma guardada ✓' : 'Sin firma'}
                status={formData.firma?.data ? 'complete' : 'empty'}
              >
                <div className="bg-zinc-50 border border-border p-6">
                  <label className="text-[10px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3 block">
                    Firma del Asesor o Cliente
                  </label>
                  <div className="bg-white border-2 border-dashed border-border mb-4">
                    <SignatureCanvas
                      ref={sigCanvas}
                      canvasProps={signatureCanvasProps}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="bg-white text-black border border-input hover:bg-zinc-50 rounded-none h-10 px-6 uppercase tracking-widest text-xs font-medium transition-all flex items-center gap-2"
                      data-testid="clear-signature"
                    >
                      <Trash2 className="w-4 h-4" />
                      Limpiar
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className="bg-black text-white hover:bg-zinc-800 rounded-none h-10 px-6 uppercase tracking-widest text-xs font-medium transition-all"
                      data-testid="save-signature"
                    >
                      Guardar Firma
                    </button>
                  </div>
                </div>
              </CollapsibleSection>

              {id && id !== 'new' && (
                <>
                  {canViewCosting() && (
                    <CollapsibleSection title="COSTEO Y PRESUPUESTO INTERNO" icon={Calculator} defaultOpen={false}>
                      <CostingModule
                        initialCosting={formData.costing}
                        onSave={(costingData) => {
                          setFormData(prev => ({
                            ...prev,
                            costing: costingData
                          }));
                        }}
                      />
                    </CollapsibleSection>
                  )}

                  <CollapsibleSection title="REFERENCIAS VISUALES" icon={Camera} defaultOpen={false}>
                    <PhotoGallery
                      sheetId={id}
                      photos={formData.photos || []}
                      onPhotoAdded={(updatedSheet) => {
                        setFormData(updatedSheet);
                        toast.success('Foto agregada');
                      }}
                      onPhotoDeleted={(photoId) => {
                        fetchSheet();
                        toast.success('Foto eliminada');
                      }}
                    />
                  </CollapsibleSection>

                  <CollapsibleSection title="HISTORIAL DE CAMBIOS" defaultOpen={false}>
                    <div className="bg-zinc-50 border border-border p-6">
                      {formData.change_history && formData.change_history.length > 0 ? (
                        <div className="space-y-3">
                          {formData.change_history.slice().reverse().map((entry, index) => (
                            <div
                              key={`history-${entry.timestamp}-${index}`}
                              className="flex items-start gap-3 pb-3 border-b border-border last:border-0"
                              data-testid={`history-entry-${index}`}
                            >
                              <Clock className="w-4 h-4 text-muted-foreground mt-1" />
                              <div className="flex-1">
                                <div className="flex items-center justify-between mb-1">
                                  <p className="text-sm font-medium text-black">{entry.action}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(entry.timestamp).toLocaleString('es-CO', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </p>
                                </div>
                                <p className="text-xs text-muted-foreground">Por: {entry.user}</p>
                                {entry.changes && (
                                  <p className="text-xs text-black mt-1">{entry.changes}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-4">
                          No hay cambios registrados aún
                      </p>
                    )}
                    </div>
                  </CollapsibleSection>
                </>
              )}


              <div className="flex flex-col sm:flex-row gap-4 mt-12 pt-8 border-t border-border">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-black text-white hover:bg-zinc-800 rounded-none h-12 uppercase tracking-widest text-xs font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="save-draft-button"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Guardando...' : 'Guardar Borrador'}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={loading}
                  className="flex-1 bg-[#D4AF37] text-black hover:bg-[#C49F27] rounded-none h-12 uppercase tracking-widest text-xs font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="finalize-button"
                >
                  <FileCheck className="w-4 h-4" />
                  Finalizar Ficha
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Modal de Cliente Existente - Mejorado con Historial */}
      {showClienteModal && clienteEncontrado?.cliente && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-lg font-medium flex items-center gap-2">
                <User className="w-5 h-5 text-amber-600" />
                Cliente Ya Registrado
              </h3>
              <button 
                onClick={() => setShowClienteModal(false)}
                className="text-muted-foreground hover:text-black"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Alerta de coincidencia */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">{clienteEncontrado.cliente.nombre}</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {clienteEncontrado.cliente.numero_documento && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {clienteEncontrado.cliente.tipo_documento || 'CC'}: {clienteEncontrado.cliente.numero_documento}
                        </span>
                      )}
                      {clienteEncontrado.cliente.telefono && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          Tel: {clienteEncontrado.cliente.telefono}
                        </span>
                      )}
                      {clienteEncontrado.cliente.correo && (
                        <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                          {clienteEncontrado.cliente.correo}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-amber-600 mt-2">
                      Coincide por: <strong>{clienteEncontrado.coincide_por?.join(', ')}</strong>
                    </p>
                    {clienteEncontrado.es_cliente_potencial && (
                      <p className="text-xs text-orange-600 mt-1">
                        ⚠️ Cliente encontrado en {clienteEncontrado.cliente.fuente === 'cotizacion' ? 'cotizaciones' : 'fichas'} existentes
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Estadísticas del cliente */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600">{clienteEncontrado.estadisticas?.total_fichas || 0}</p>
                  <p className="text-xs text-muted-foreground">Fichas</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-600">{clienteEncontrado.estadisticas?.total_cotizaciones || 0}</p>
                  <p className="text-xs text-muted-foreground">Cotizaciones</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{clienteEncontrado.estadisticas?.total_ventas || 0}</p>
                  <p className="text-xs text-muted-foreground">Ventas</p>
                </div>
                <div className="bg-zinc-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-600">
                    ${((clienteEncontrado.estadisticas?.valor_total_vendido || 0) / 1000000).toFixed(1)}M
                  </p>
                  <p className="text-xs text-muted-foreground">Valor Vendido</p>
                </div>
              </div>
              
              {/* Última cotización */}
              {clienteEncontrado.ultima_cotizacion && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700 mb-1 font-medium">Última cotización:</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-blue-900">
                        {clienteEncontrado.ultima_cotizacion.numero}
                      </p>
                      <p className="text-xs text-blue-600">
                        Estado: <span className="capitalize">{clienteEncontrado.ultima_cotizacion.estado}</span>
                        {clienteEncontrado.ultima_cotizacion.fecha_evento && (
                          <> · Evento: {new Date(clienteEncontrado.ultima_cotizacion.fecha_evento).toLocaleDateString('es-CO')}</>
                        )}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-blue-900">
                      ${clienteEncontrado.ultima_cotizacion.total?.toLocaleString('es-CO')}
                    </p>
                  </div>
                  {clienteEncontrado.ultima_cotizacion.vendedor_nombre && (
                    <p className="text-xs text-blue-600 mt-1">
                      Vendedor: {clienteEncontrado.ultima_cotizacion.vendedor_nombre}
                    </p>
                  )}
                </div>
              )}
              
              {/* Referencias de tela de cotizaciones anteriores */}
              {clienteEncontrado.referencias_tela?.length > 0 && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-xs text-purple-700 mb-2 font-medium flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    Telas cotizadas anteriormente:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {clienteEncontrado.referencias_tela.slice(0, 8).map((ref) => (
                      <span key={ref} className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded">
                        {ref}
                      </span>
                    ))}
                    {clienteEncontrado.referencias_tela.length > 8 && (
                      <span className="text-xs text-purple-600">
                        +{clienteEncontrado.referencias_tela.length - 8} más
                      </span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Historial de cotizaciones */}
              {clienteEncontrado.cotizaciones?.length > 1 && (
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Historial de cotizaciones:</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {clienteEncontrado.cotizaciones.slice(0, 5).map((cot) => (
                      <div key={cot.numero || cot.id} className="flex items-center justify-between text-xs p-2 bg-zinc-50 rounded">
                        <span className="font-medium">{cot.numero}</span>
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                          cot.estado === 'aprobada' || cot.es_venta 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : cot.estado === 'enviada'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-zinc-200 text-zinc-700'
                        }`}>
                          {cot.es_venta ? 'VENTA' : cot.estado}
                        </span>
                        <span className="text-muted-foreground">
                          ${cot.total?.toLocaleString('es-CO')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Seguimientos pendientes */}
              {clienteEncontrado.seguimientos_pendientes?.length > 0 && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-xs text-orange-700 mb-1 font-medium flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Seguimientos pendientes ({clienteEncontrado.seguimientos_pendientes.length})
                  </p>
                  {clienteEncontrado.seguimientos_pendientes.slice(0, 2).map((seg) => (
                    <p key={seg.cotizacion_numero || seg.id} className="text-xs text-orange-600">
                      • {seg.cotizacion_numero}: {seg.nota || 'Pendiente de seguimiento'}
                    </p>
                  ))}
                </div>
              )}
              
              {/* Mensaje de acción */}
              <div className="bg-zinc-100 rounded-lg p-3 mb-4">
                <p className="text-sm text-zinc-700">
                  <strong>¿Qué desea hacer?</strong>
                </p>
                <p className="text-xs text-zinc-600 mt-1">
                  Al asociar el cliente, se cargarán automáticamente sus datos, referencias de tela cotizadas y notas anteriores.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowClienteModal(false)}
                  className="flex-1 px-4 py-2.5 border border-border rounded-lg text-sm hover:bg-zinc-50 transition-colors"
                >
                  Continuar sin asociar
                </button>
                <button
                  onClick={asociarClienteExistente}
                  className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors font-medium"
                  data-testid="asociar-cliente-ficha-btn"
                >
                  Sí, cargar datos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de búsqueda de cotizaciones para asociar */}
      {showBuscarCotizacion && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border bg-blue-50">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium flex items-center gap-2 text-blue-800">
                  <FileCheck className="w-5 h-5" />
                  Buscar Cotización para Asociar
                </h3>
                <button
                  onClick={() => {
                    setShowBuscarCotizacion(false);
                    setBusquedaCotizacion('');
                    setCotizacionesEncontradas([]);
                  }}
                  className="p-1 hover:bg-blue-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-blue-600" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* Campo de búsqueda */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Buscar por nombre, documento, teléfono, correo o número de cotización
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={busquedaCotizacion}
                    onChange={(e) => setBusquedaCotizacion(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        buscarCotizacionesParaAsociar(busquedaCotizacion);
                      }
                    }}
                    placeholder="Ej: COT-2026-0001, Marco Hernández, 3001234567..."
                    className="flex-1 px-4 py-2.5 border border-zinc-300 rounded-lg focus:outline-none focus:border-blue-500"
                    data-testid="input-buscar-cotizacion"
                  />
                  <button
                    onClick={() => buscarCotizacionesParaAsociar(busquedaCotizacion)}
                    disabled={buscandoCotizaciones || busquedaCotizacion.length < 3}
                    className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-zinc-300 transition-colors flex items-center gap-2"
                  >
                    {buscandoCotizaciones ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Buscar'
                    )}
                  </button>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Mínimo 3 caracteres para buscar</p>
              </div>
              
              {/* Resultados de búsqueda */}
              {cotizacionesEncontradas.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-zinc-700">
                    {cotizacionesEncontradas.length} cotización(es) encontrada(s)
                  </p>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {cotizacionesEncontradas.map((cot) => (
                      <div
                        key={cot.id}
                        className="p-4 border border-zinc-200 rounded-lg hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer"
                        onClick={() => asociarCotizacion(cot)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-zinc-900">{cot.numero}</p>
                            <p className="text-sm text-zinc-600">{cot.cliente_nombre}</p>
                            <p className="text-xs text-zinc-500">
                              Doc: {cot.cliente_documento} · Tel: {cot.cliente_telefono}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-emerald-600">
                              ${cot.total?.toLocaleString('es-CO')}
                            </p>
                            <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                              cot.estado === 'aprobada' || cot.es_venta
                                ? 'bg-emerald-100 text-emerald-700'
                                : cot.estado === 'enviada'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-zinc-100 text-zinc-600'
                            }`}>
                              {cot.es_venta ? 'Venta' : cot.estado}
                            </span>
                          </div>
                        </div>
                        
                        {/* Referencias de tela */}
                        {cot.referencias_tela?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-zinc-100">
                            <p className="text-xs text-amber-700">
                              <Package className="w-3 h-3 inline mr-1" />
                              Telas: {cot.referencias_tela.join(', ')}
                            </p>
                          </div>
                        )}
                        
                        {/* Advertencia si ya tiene ficha */}
                        {cot.ficha_asociada && (
                          <div className="mt-2 p-2 bg-amber-50 rounded text-xs text-amber-700">
                            ⚠️ Esta cotización ya tiene ficha asociada ({cot.ficha_asociada.pedido || cot.ficha_asociada.id?.slice(0,8)})
                          </div>
                        )}
                        
                        <p className="text-xs text-blue-600 mt-2">Click para asociar esta cotización</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : busquedaCotizacion.length >= 3 && !buscandoCotizaciones ? (
                <div className="text-center py-8 text-zinc-500">
                  <FileCheck className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No se encontraron cotizaciones</p>
                  <p className="text-sm">Intenta con otro término de búsqueda</p>
                </div>
              ) : null}
            </div>
            
            <div className="p-4 border-t border-zinc-200 bg-zinc-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowBuscarCotizacion(false);
                  setBusquedaCotizacion('');
                  setCotizacionesEncontradas([]);
                }}
                className="px-4 py-2 border border-zinc-300 rounded-lg text-sm hover:bg-zinc-100 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cambios sin guardar */}
      {showUnsavedModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl overflow-hidden">
            <div className="p-6 border-b border-border bg-amber-50">
              <h3 className="text-lg font-medium flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                Cambios sin guardar
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-zinc-700 mb-6">
                Hay cambios sin guardar en esta ficha. Si sales ahora, se perderán los cambios realizados. 
                <br/><br/>
                <strong>¿Qué deseas hacer?</strong>
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={async () => {
                    setShowUnsavedModal(false);
                    // Guardar primero
                    const fakeEvent = { preventDefault: () => {} };
                    await handleSubmit(fakeEvent, false);
                    // Si se guardó exitosamente, proceder con la navegación
                    if (pendingNavigation) pendingNavigation();
                  }}
                  className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
                  data-testid="guardar-y-salir-btn"
                >
                  <Save className="w-4 h-4" />
                  Guardar y salir
                </button>
                <button
                  onClick={() => {
                    setShowUnsavedModal(false);
                    setHasUnsavedChanges(false);
                    if (pendingNavigation) pendingNavigation();
                  }}
                  className="w-full px-4 py-3 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 transition-colors font-medium"
                  data-testid="salir-sin-guardar-btn"
                >
                  Salir sin guardar
                </button>
                <button
                  onClick={() => {
                    setShowUnsavedModal(false);
                    setPendingNavigation(null);
                  }}
                  className="w-full px-4 py-3 border border-border rounded-lg text-sm hover:bg-zinc-50 transition-colors"
                  data-testid="cancelar-salida-btn"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barra flotante de guardar - Siempre visible */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 shadow-2xl z-40 md:hidden">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            disabled={loading}
            className={`flex-1 h-12 rounded-lg font-medium text-sm transition-all flex items-center justify-center gap-2 ${
              hasUnsavedChanges
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/30'
                : 'bg-zinc-200 text-zinc-500'
            }`}
            data-testid="floating-save-btn"
          >
            <Save className="w-4 h-4" />
            {loading ? 'Guardando...' : (hasUnsavedChanges ? 'Guardar cambios' : 'Guardado')}
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e, true)}
            disabled={loading}
            className="px-4 h-12 bg-[#D4AF37] text-black rounded-lg font-medium text-sm hover:bg-[#C49F27] transition-all flex items-center justify-center gap-2"
            data-testid="floating-finalize-btn"
          >
            <FileCheck className="w-4 h-4" />
            Finalizar
          </button>
        </div>
      </div>

      {/* Indicador de cambios sin guardar - Desktop */}
      {hasUnsavedChanges && (
        <div className="hidden md:block fixed bottom-6 right-6 z-40">
          <button
            type="button"
            onClick={(e) => handleSubmit(e, false)}
            disabled={loading}
            className="flex items-center gap-3 px-6 py-3 bg-emerald-600 text-white rounded-full shadow-2xl hover:bg-emerald-700 transition-all font-medium animate-pulse"
            data-testid="desktop-floating-save"
          >
            <Save className="w-5 h-5" />
            {loading ? 'Guardando...' : 'Guardar cambios'}
            <span className="w-2 h-2 bg-white rounded-full animate-ping" />
          </button>
        </div>
      )}

      {/* Modal de confirmación para finalizar sin cotización */}
      {showConfirmarSinCotizacion && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl overflow-hidden">
            <div className="p-6 border-b border-border bg-amber-50">
              <h3 className="text-lg font-medium flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                Ficha sin cotización asociada
              </h3>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-zinc-700 mb-4">
                Esta ficha no está asociada a ninguna cotización. 
              </p>
              <p className="text-sm text-zinc-700 mb-6">
                <strong>¿Deseas asociarla antes de finalizar?</strong>
              </p>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowConfirmarSinCotizacion(false);
                    setShowBuscarCotizacion(true);
                  }}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                  data-testid="btn-asociar-cotizacion-modal"
                >
                  <FileCheck className="w-4 h-4" />
                  Asociar cotización
                </button>
                <button
                  onClick={(e) => {
                    setShowConfirmarSinCotizacion(false);
                    // Forzar la finalización sin re-validar campos pendientes
                    handleSubmit(e, true, true);
                  }}
                  className="w-full px-4 py-3 bg-amber-100 text-amber-800 rounded-lg text-sm hover:bg-amber-200 transition-colors font-medium"
                  data-testid="btn-continuar-sin-cotizacion"
                >
                  Continuar como ficha independiente
                </button>
                <button
                  onClick={() => setShowConfirmarSinCotizacion(false)}
                  className="w-full px-4 py-3 border border-border rounded-lg text-sm hover:bg-zinc-50 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODAL DE ALERTA DE CAMPOS PENDIENTES ==================== */}
      {showAlertaPendientes && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" data-testid="modal-campos-pendientes">
          <div className="bg-white rounded-xl max-w-lg w-full shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="p-5 border-b border-red-200 bg-gradient-to-r from-red-50 to-amber-50">
              <h3 className="text-lg font-semibold flex items-center gap-3 text-red-800">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                Campos Pendientes por Completar
              </h3>
              <p className="text-sm text-red-600 mt-2 font-medium">
                Total: {validacionPendientes.totalPendientes} campo(s) sin completar en {validacionPendientes.secciones?.length || 0} sección(es)
              </p>
            </div>
            
            {/* Lista de campos pendientes por sección */}
            <div className="p-5 overflow-y-auto flex-1">
              <p className="text-sm text-zinc-600 mb-4">
                Revisa y completa los siguientes campos antes de finalizar:
              </p>
              
              <div className="space-y-4">
                {validacionPendientes.secciones?.map((seccion, index) => {
                  // Determinar colores por tipo de sección
                  const colores = {
                    basico: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
                    camisa: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' },
                    blazer: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' },
                    pantalon: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
                    chaleco: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-800' },
                    chaqueta: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-800' }
                  };
                  
                  const color = colores[seccion.tipo] || colores.basico;
                  
                  return (
                    <div 
                      key={seccion.tipo || seccion.seccion || `seccion-${index}`}
                      className={`rounded-lg border-2 ${color.border} ${color.bg} overflow-hidden`}
                      data-testid={`seccion-pendiente-${index}`}
                    >
                      {/* Header de la sección */}
                      <div className={`px-4 py-2.5 flex items-center justify-between ${color.text} border-b ${color.border}`}>
                        <div className="flex items-center gap-2 font-semibold">
                          <span className="text-lg">{seccion.icono}</span>
                          <span>{seccion.seccion}</span>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${color.badge}`}>
                          {seccion.cantidad} pendiente{seccion.cantidad !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {/* Lista de campos */}
                      <div className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          {seccion.campos.map((campo) => (
                            <span 
                              key={campo}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium ${color.badge}`}
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-60"></span>
                              {campo}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Leyenda */}
              <div className="mt-5 pt-4 border-t border-zinc-200">
                <p className="text-xs text-zinc-500 mb-2 font-medium">Leyenda de secciones:</p>
                <div className="flex flex-wrap gap-2">
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-red-300"></span> Datos básicos
                  </span>
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-blue-300"></span> Camisa
                  </span>
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-purple-300"></span> Blazer
                  </span>
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-amber-300"></span> Pantalón
                  </span>
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-emerald-300"></span> Chaleco
                  </span>
                  <span className="flex items-center gap-1.5 text-xs">
                    <span className="w-3 h-3 rounded bg-indigo-300"></span> Chaqueta
                  </span>
                </div>
              </div>
            </div>
            
            {/* Footer con botones */}
            <div className="p-5 border-t border-zinc-200 bg-zinc-50 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowAlertaPendientes(false);
                  setValidacionPendientes({ totalPendientes: 0, secciones: [] });
                  setAccionPendiente(null);
                }}
                className="flex-1 px-4 py-3 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors font-medium flex items-center justify-center gap-2"
                data-testid="btn-completar-campos"
              >
                <PenTool className="w-4 h-4" />
                Completar campos
              </button>
              <button
                onClick={(e) => {
                  setShowAlertaPendientes(false);
                  setValidacionPendientes({ totalPendientes: 0, secciones: [] });
                  // Forzar el guardado/finalización con campos pendientes
                  if (accionPendiente === 'finalizar') {
                    handleSubmit(e, true, true); // forzarGuardado = true
                  }
                  setAccionPendiente(null);
                }}
                className="flex-1 px-4 py-3 bg-amber-100 text-amber-800 rounded-lg text-sm hover:bg-amber-200 transition-colors font-medium flex items-center justify-center gap-2"
                data-testid="btn-continuar-con-pendientes"
              >
                <AlertTriangle className="w-4 h-4" />
                Finalizar de todos modos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
